import React, { useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Avatar
} from '@mui/material';
import { 
  Article as BlogIcon,
  Work as ProjectIcon,
  Timeline as ExperienceIcon,
  Event as ActivityIcon,
  People as UsersIcon,
  Build as TechIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

const buildAdminModules = (t) => ([
  { 
    title: t('admin.sections.projects'), 
    description: t('admin.dashboard.modules.projects'), 
    icon: <ProjectIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/projects',
    color: '#0FA3A3'
  },
  { 
    title: t('admin.sections.blog'), 
    description: t('admin.dashboard.modules.blog'), 
    icon: <BlogIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/blog',
    color: '#D4AF37'
  },
  { 
    title: t('admin.sections.experiences'), 
    description: t('admin.dashboard.modules.experiences'), 
    icon: <ExperienceIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/experiences',
    color: '#8B5CF6'
  },
  { 
    title: t('admin.sections.skills'), 
    description: t('admin.dashboard.modules.skills'), 
    icon: <AutoFixHighIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/skills',
    color: '#D4AF37'
  },
  { 
    title: t('admin.sections.activities'), 
    description: t('admin.dashboard.modules.activities'), 
    icon: <ActivityIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/activities',
    color: '#EC4899'
  },
  { 
    title: t('admin.sections.technologies'), 
    description: t('admin.dashboard.modules.technologies'), 
    icon: <TechIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/technologies',
    color: '#10B981'
  },
  { 
    title: t('admin.sections.cases'), 
    description: t('admin.dashboard.modules.cases'), 
    icon: <ProjectIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/cases',
    color: '#0FA3A3'
  },
  { 
    title: t('admin.sections.messages'), 
    description: t('admin.dashboard.modules.messages'), 
    icon: <UsersIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/messages',
    color: '#F59E0B'
  },
  { 
    title: "Adaptation CV IA", 
    description: "Adapter un CV à une offre d'emploi avec l'IA", 
    icon: <AutoFixHighIcon sx={{ fontSize: 40 }} />, 
    path: '/admin/cv-adaptation',
    color: '#0FA3A3'
  },
]);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const adminModules = buildAdminModules(t);

  useEffect(() => {
    localStorage.removeItem('isGuest');
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0A0A', pt: 12, pb: 8 }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <DashboardIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                  {t('admin.dashboard.title')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                  {t('admin.dashboard.welcome')}, {user?.email}
                </Typography>
              </Box>
            </Box>
            <Button 
              variant="outlined" 
              startIcon={<LogoutIcon />} 
              onClick={handleLogout}
              sx={{ 
                color: '#EF4444', 
                borderColor: 'rgba(239, 68, 68, 0.5)',
                '&:hover': {
                  borderColor: '#EF4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)'
                }
              }}
            >
              {t('admin.actions.logout')}
            </Button>
          </Box>

          <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 6 }}>
            {t('admin.dashboard.selectModule')}
          </Typography>

          <Grid container spacing={3}>
            {adminModules.map((module, index) => (
              <Grid item xs={12} sm={6} md={4} key={module.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 30px ${module.color}20`,
                        borderColor: module.color
                      }
                    }}
                  >
                    <CardActionArea onClick={() => navigate(module.path)}>
                      <CardContent sx={{ p: 4 }}>
                        <Box 
                          sx={{ 
                            display: 'inline-flex', 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: `${module.color}20`,
                            color: module.color,
                            mb: 2
                          }}
                        >
                          {module.icon}
                        </Box>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                          {module.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                          {module.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
